import { Collapse } from '@mui/material'
import {
  FAILED_OPT_JOB_STATUSES,
  OptJobType,
  OptJobWithQueueItems,
  TERMINAL_OPT_JOB_STATUSES,
} from 'api/optJobs/optJobs.api'
import { getOptimizationFailureExplanationKey } from 'helpers/optimize.helper/optimize.helper'
import { Dispatch, ReactNode, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import Icon from 'ui/atoms/Icon/Icon'
import StatusIcon, { StatusIconVariants } from 'ui/atoms/StatusIcon/StatusIcon'
import { getStatusIconVariant } from 'ui/atoms/StatusIcon/StatusIcon.helper'
import { useNowTicker } from 'ui/components/OptJobPhaseProgress/hooks/useNowTicker'
import { formatElapsed } from 'ui/components/OptJobPhaseProgress/OptJobPhaseProgress.helper'
import OptimizationFailedGuide from 'ui/molecules/OptimizationFailedGuide/OptimizationFailedGuide'
import { toOptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.helper'
import { OptJobOutcome } from 'ui/optJobOutcome/optJobOutcome.types'
import Datetime from 'utils/datetime/datetime'
import { useAvailableSubtypes } from 'utils/systemUtils'
import { getTime } from '../NotificationCenter.helper'
import { OptJobLogPhaseProgress } from './OptJobLogPhaseProgress'
import {
  deriveOptJobLogRowViewModel,
  deriveTitleSubtypeName,
  getLinkToOptJobTypeModule,
  isPublishedPlanJob,
  mapOptJobTypeNameAndStatusToText,
  OptJobCreator,
} from './OptJobLogRow.helper'

type OptJobLogRowProps = {
  treatRegularAsPublished: boolean
  optJobWithQueueItems: OptJobWithQueueItems
}

const OUTCOME_TEXT_CLASS: Record<OptJobOutcome, string> = {
  created: 'text-gray-400',
  finished: 'text-success-4',
  failed: 'text-danger-3',
  unsolvable: 'text-warning-5',
}

function CreatorLabel({ creator }: { creator: OptJobCreator }): ReactNode {
  const { t } = useTranslation()

  if (creator.kind === 'automatic') return <span>{t('Automatic')}</span>

  return <span>{`${t('Created by')} ${creator.name ?? t('Unknown user')}`}</span>
}

function RowTitle({
  variant,
  text,
  failureExplanationKey,
}: {
  variant: StatusIconVariants
  text: string
  /** Only a job that ended without a result has anything to explain; StatusIcon resolves the key. */
  failureExplanationKey?: string
}): ReactNode {
  return (
    <div className="flex items-start gap-2">
      <StatusIcon variant={variant} tooltip={failureExplanationKey ? { title: failureExplanationKey } : undefined} />
      <span style={{ fontSize: '15px' }} className="flex-1 min-w-0 max-w-[280px] wrap-break-word">
        {text}
      </span>
    </div>
  )
}

function RowFooter({ createdAt, creator }: { createdAt: ISODateTime; creator: OptJobCreator }): ReactNode {
  return (
    <div className="flex items-center gap-1.5 px-2 mt-1 ml-9 text-xs italic text-gray-400">
      {getTime(createdAt, undefined, true)}
      <span>&middot;</span>
      <CreatorLabel creator={creator} />
    </div>
  )
}

type OptJobPeriod = { startTime: ISODateTime; endTime: ISODateTime }

function RowPeriod({ period }: { period: OptJobPeriod }): ReactNode {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-1.5 px-2 text-xs ml-7 italic">
      <span>
        {t('{{startTime}} to {{endTime}}', {
          startTime: Datetime.toLocalTime(period.startTime),
          endTime: Datetime.toLocalTime(Datetime.ISODatetimeTo59Minutes(period.endTime)),
        })}
      </span>
    </div>
  )
}

function CollapseChevron({
  expanded,
  setExpanded,
  label,
}: {
  expanded: boolean
  setExpanded: Dispatch<SetStateAction<boolean>>
  label: string
}): ReactNode {
  return (
    <button
      type="button"
      onClick={() => setExpanded((prev) => !prev)}
      className="flex items-center gap-1.5 text-xs hover:underline cursor-pointer bg-transparent border-none p-0"
    >
      <i className={`fal fa-chevron-right text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`} />
      <span>{label}</span>
    </button>
  )
}

/** Job types whose result is scoped to a period, which the row spells out. */
const JOBS_THAT_SHOW_PERIOD = new Set<OptJobType>([
  'followup_with_deviations',
  'followup_without_deviations',
  'followup_regular',
  'settlement',
])

export function OptJobLogRow({ treatRegularAsPublished, optJobWithQueueItems }: OptJobLogRowProps): ReactNode {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const availableSubtypes = useAvailableSubtypes(optJobWithQueueItems.opt_job_type)
  const isPublishAction = isPublishedPlanJob(optJobWithQueueItems.opt_job_type, treatRegularAsPublished)
  // Publishing a plan has no solve behind it, so there are no phases to break down.
  const showPhaseBreakdown = !isPublishAction

  const terminal = TERMINAL_OPT_JOB_STATUSES.has(optJobWithQueueItems.status)

  const text = mapOptJobTypeNameAndStatusToText({
    optJobType: optJobWithQueueItems.opt_job_type,
    status: optJobWithQueueItems.status,
    subtypeDisplayName: deriveTitleSubtypeName({
      subtypes: optJobWithQueueItems.subtypes,
      availableSubtypes,
      namesEverySubtype: isPublishAction,
    }),
    treatRegularAsPublished,
  })
  const now = useNowTicker({ isTicking: !terminal })
  const viewModel = deriveOptJobLogRowViewModel(optJobWithQueueItems, now, { isPublishAction })
  const statusIconVariant = getStatusIconVariant(
    optJobWithQueueItems.opt_job_type,
    optJobWithQueueItems.status,
    treatRegularAsPublished
  )

  const jobIsFailed = FAILED_OPT_JOB_STATUSES.has(optJobWithQueueItems.status)
  const outcome = toOptJobOutcome(optJobWithQueueItems.status, isPublishAction)
  const failureExplanationKey = getOptimizationFailureExplanationKey(optJobWithQueueItems.status)
  const { progress } = viewModel
  const isMulti = progress.kind === 'scenarios'

  // A job still being created has no queue item to read a period from, and the job
  // types whose result is not scoped to one leave both stamps null — neither has a
  // period to show or to carry into a module link.
  const [firstQueueItem] = optJobWithQueueItems.queue_items

  const period: OptJobPeriod | null =
    firstQueueItem?.opt_start_time != null && firstQueueItem.opt_end_time != null
      ? {
          startTime: Datetime.toISOString(firstQueueItem.opt_start_time),
          endTime: Datetime.toISOString(firstQueueItem.opt_end_time),
        }
      : null
  const periodToShow = JOBS_THAT_SHOW_PERIOD.has(optJobWithQueueItems.opt_job_type) ? period : null

  if (viewModel.kind === 'finished') {
    return (
      <div className="mb-4">
        <RowTitle variant={statusIconVariant} text={text} failureExplanationKey={failureExplanationKey} />
        <div className={`flex items-center gap-1.5 px-2 text-xs ml-7 ${OUTCOME_TEXT_CLASS[outcome]}`}>
          <span className="flex items-center">
            <Icon icon="far fa-clock" />
            {getTime(viewModel.finishedAt, undefined, true)}
          </span>
          {isMulti && (
            <>
              <span>&middot;</span>
              <span>{`${progress.finished}/${progress.total} ${t('scenarios')}`}</span>
            </>
          )}
          <span>&middot;</span>
          <span>{`${formatElapsed(viewModel.elapsedMs)} ${t('total')}`}</span>
        </div>
        {periodToShow != null && <RowPeriod period={periodToShow} />}
        {showPhaseBreakdown && (
          <>
            <div className="flex items-center gap-2 px-2 mt-1 ml-9">
              <CollapseChevron
                expanded={expanded}
                setExpanded={setExpanded}
                label={isMulti ? t('Per scenario times') : t('Phase timings')}
              />
            </div>

            <Collapse in={expanded} unmountOnExit>
              <div className="mt-2 px-2 pb-2">
                <OptJobLogPhaseProgress optJobWithQueueItems={optJobWithQueueItems} now={now} />
              </div>
            </Collapse>
          </>
        )}
        <RowFooter createdAt={optJobWithQueueItems.created_at} creator={viewModel.creator} />
        {jobIsFailed ? (
          <OptimizationFailedGuide startTime={period?.startTime} endTime={period?.endTime} />
        ) : (
          <div className="mt-2 text-xs text-right ml-10">
            {getLinkToOptJobTypeModule(optJobWithQueueItems.opt_job_type, navigate, period)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mb-4">
      <RowTitle variant={statusIconVariant} text={text} failureExplanationKey={failureExplanationKey} />
      <div className="flex items-center gap-1.5 px-2 text-xs ml-7 italic weight-normal text-gray-400">
        <span className="flex items-center font-bold text-info-3">
          <Icon icon="far fa-clock" />
          {`${formatElapsed(viewModel.elapsedMs)} ${t('total')}`}
        </span>
        {isMulti && (
          <>
            <span>&middot;</span>
            <span>{`${progress.total} ${t('scenarios')}`}</span>
          </>
        )}
      </div>
      {periodToShow != null && <RowPeriod period={periodToShow} />}
      {showPhaseBreakdown && (
        <>
          <div className="flex items-center gap-2 px-2 mt-1 ml-9 text-info-3">
            <CollapseChevron
              expanded={expanded}
              setExpanded={setExpanded}
              label={
                progress.kind === 'scenarios'
                  ? `${t('Phase timings')} ${progress.finished}/${progress.total} ${t('done')}`
                  : `${t('Show steps')} ${progress.done}/${progress.total} ${t('done')}`
              }
            />
          </div>
          <Collapse in={expanded} unmountOnExit>
            <div className="mt-2 px-2 pb-2">
              <OptJobLogPhaseProgress optJobWithQueueItems={optJobWithQueueItems} now={now} />
            </div>
          </Collapse>
        </>
      )}
      <RowFooter createdAt={optJobWithQueueItems.created_at} creator={viewModel.creator} />
    </div>
  )
}
